import React, { useEffect, useState } from 'react'
import {
	useDeleteVacancyMutation,
	useGetMatchedCandidatesQuery,
	useGetVacancyQuery,
	useSearchCandidatesMutation,
} from '../store/api/vacanciesApi'
import { useParams } from 'react-router-dom'
import {
	Box,
	Breadcrumbs,
	Button,
	Card,
	CardActionArea,
	CardContent,
	Chip,
	IconButton,
	Link,
	Pagination,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import SchoolIcon from '@mui/icons-material/School'
import LanguageIcon from '@mui/icons-material/Language'
import LinearScaleIcon from '@mui/icons-material/LinearScale'
import VacancyForm from '../components/VacancyForm'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { useNavigate } from 'react-router'

const VacancyPage = () => {
	const params = useParams()
	const [isEditMode, setIsEditMode] = useState(false)
	const [page, setPage] = useState(1)
	const { data, isLoading, isError } = useGetVacancyQuery(params.id)
	const { data: matchedCandidates, isFetching: isLoadingCandidates } =
		useGetMatchedCandidatesQuery({ id: params.id, page, page_size: 10 })
	const [startSearch, { isLoading: isSearching }] =
		useSearchCandidatesMutation()
	const navigate = useNavigate()
	const [
		fetchDelete,
		{ isLoading: isLoadingDelete, isSuccess: isSuccessDelete },
	] = useDeleteVacancyMutation()

	const formatSalary = (
		min: number | null,
		max: number | null,
		currency: string,
	) => {
		if (min === null && max === null) return 'Зарплата не вказана'
		if (min !== null && max !== null) return `${min} - ${max} ${currency}`
		if (min !== null) return `З ${min} ${currency}`
		if (max !== null) return `До ${max} ${currency}`
		return ''
	}

	useEffect(() => {
		if (!isLoading && isError) {
			navigate('/vacancies')
		}
	}, [isLoading])

	useEffect(() => {
		if (!isLoadingDelete && isSuccessDelete) {
			navigate('/vacancies')
		}
	}, [isLoadingDelete])

	if (isEditMode && data) {
		return (
			<VacancyForm data={data} returnToVacancy={() => setIsEditMode(false)} />
		)
	}

	return (
		<Box>
			<Breadcrumbs sx={{ mb: '16px' }} separator="›" aria-label="breadcrumb">
				<Link
					underline="hover"
					color="inherit"
					onClick={() => navigate('/vacancies')}
				>
					Вакансії
				</Link>
				<Typography sx={{ color: 'text.primary' }}>
					{isLoading || !data ? (
						<Skeleton
							variant="text"
							sx={{ fontSize: '16px', width: '160px' }}
						/>
					) : (
						data?.title
					)}
				</Typography>
			</Breadcrumbs>
			{isLoading || !data ? (
				<Skeleton variant="rectangular" height={500} />
			) : (
				<Card>
					<CardContent>
						<Box sx={{ display: 'flex' }}>
							<Box sx={{ maxWidth: '60%', width: '100%' }}>
								<Box sx={{ display: 'flex', alignItems: 'center' }}>
									<Typography variant="h4">{data.title}</Typography>
									<Stack sx={{ ml: '12px' }} spacing={2} direction={'row'}>
										<IconButton
											onClick={() => fetchDelete(data.id)}
											loading={isLoadingDelete}
											color="error"
										>
											<DeleteIcon fontSize="inherit" />
										</IconButton>
										<IconButton
											onClick={() => setIsEditMode(true)}
											color="primary"
										>
											<EditIcon fontSize="inherit" />
										</IconButton>
									</Stack>
								</Box>
								<Typography sx={{ color: 'text.secondary', mb: 1.5 }}>
									{data.company}
								</Typography>
								<Box
									sx={{
										display: 'flex',
										flexWrap: 'wrap',
										gap: '22px',
										color: 'text.secondary',
										maxWidth: '500px',
									}}
								>
									{(data.location && data.location !== 'Не вказано') && (
										<Typography sx={{ display: 'flex', alignItems: 'center' }}>
											<LocationOnIcon color={'primary'} sx={{ mr: '3px' }} />
											{data.location}
										</Typography>
									)}
									<Typography sx={{ display: 'flex', alignItems: 'center' }}>
										<MapsHomeWorkIcon color={'primary'} sx={{ mr: '3px' }} />{' '}
										{data.is_remote ? 'Віддалено' : 'Офіс'}
									</Typography>
									<Typography sx={{ display: 'flex', alignItems: 'center' }}>
										<MonetizationOnIcon color={'primary'} sx={{ mr: '3px' }} />
										{formatSalary(
											data.salary_min,
											data.salary_max,
											data.salary_currency,
										)}
									</Typography>
									{(data.employment_type && data.employment_type !== 'Не вказано') && (
										<Typography sx={{ display: 'flex', alignItems: 'center' }}>
											<AccessTimeIcon color={'primary'} sx={{ mr: '3px' }} />
											{data.employment_type}
										</Typography>
									)}
									{(data.schedule && data.schedule !== 'Не вказано') && (
										<Typography sx={{ display: 'flex', alignItems: 'center' }}>
											<FactCheckIcon color={'primary'} sx={{ mr: '3px' }} />
											{data.schedule}
										</Typography>
									)}
									{(data.education_level_required && data.education_level_required !== 'Не вказано') && (
										<Typography sx={{ display: 'flex', alignItems: 'center' }}>
											<SchoolIcon color={'primary'} sx={{ mr: '3px' }} />
											{data.education_level_required}
										</Typography>
									)}
									{(data.languages_required && data.languages_required !== 'Не вказано') && (
										<Typography sx={{ display: 'flex', alignItems: 'center' }}>
											<LanguageIcon color={'primary'} sx={{ mr: '3px' }} />
											{data.languages_required}
										</Typography>
									)}
									{data.experience_required && (
										<Typography sx={{ display: 'flex', alignItems: 'center' }}>
											<LinearScaleIcon color={'primary'} sx={{ mr: '3px' }} />
											{data.experience_required}
										</Typography>
									)}
								</Box>
								{data.must_have_skills && (
									<Stack
										direction="row"
										spacing={1}
										sx={{ mt: '12px', alignItems: 'center' }}
									>
										<Typography variant="subtitle2">
											Обов&#39;язкові навички:
										</Typography>
										{data.must_have_skills.map((skill, index) => (
											<Chip key={index} label={skill} />
										))}
										{data.must_have_skills.length === 0 && (<Typography color={'textSecondary'}>Відсутні</Typography>)}
									</Stack>
								)}
								{data.nice_to_have_skills && (
									<Stack
										direction="row"
										spacing={1}
										sx={{ mt: '12px', alignItems: 'center' }}
									>
										<Typography variant="subtitle2">Бажані навички:</Typography>
										{data.nice_to_have_skills.map((skill, index) => (
											<Chip variant={'outlined'} key={index} label={skill} />
										))}
										{data.must_have_skills.length === 0 && (<Typography color={'textSecondary'}>Відсутні</Typography>)}
									</Stack>
								)}
								<Typography sx={{ mt: '12px', maxWidth: '800px' }}>
									{data.original_text}
								</Typography>
							</Box>
							<Card
								sx={{
									width: '100%',
									ml: '16px',
									backgroundColor: 'background.default',
								}}
							>
								<CardContent>
									<Typography variant="h6" sx={{ mb: '16px' }}>
										Запропоновані кандидати
									</Typography>
									<Box sx={{mb: '22px'}}>
										<Button
											variant="outlined"
											onClick={() => startSearch(params.id)}
											loading={isSearching}
											fullWidth
										>
											Запустити пошук
										</Button>
										{matchedCandidates && matchedCandidates.count >0 && <Typography color='textSecondary'>
                                            Останній пошук: {new Date(matchedCandidates.results[0].matched_at).toLocaleString()}
										</Typography>}
									</Box>
									<Stack spacing={1}>
										{!isLoadingCandidates &&
											matchedCandidates &&
											matchedCandidates.results.map((candidate) => (
												<Card key={candidate.candidate_id}>
													<CardActionArea
														onClick={() =>
															navigate(
																`/vacancies/${params.id}/candidate/${candidate.candidate_id}`,
															)
														}
													>
														<CardContent>
															<Typography variant="h6">
																{candidate.first_name +
																	' ' +
																	candidate.last_name}
															</Typography>
															<Typography>
																Сумістність:{' '}
																<Typography
																	color={
																		candidate.match_percentage >= 75
																			? 'green'
																			: candidate.match_percentage >= 50
																				? 'orange'
																				: 'red'
																	}
																	component="span"
																>
																	{candidate.match_percentage}%
																</Typography>
															</Typography>
														</CardContent>
													</CardActionArea>
												</Card>
											))}
										{isLoadingCandidates && (
											<>
												<Skeleton
													variant="rectangular"
													width={'100%'}
													height={'88px'}
												/>
												<Skeleton
													variant="rectangular"
													width={'100%'}
													height={'88px'}
												/>
												<Skeleton
													variant="rectangular"
													width={'100%'}
													height={'88px'}
												/>
												<Skeleton
													variant="rectangular"
													width={'100%'}
													height={'88px'}
												/>
												<Skeleton
													variant="rectangular"
													width={'100%'}
													height={'88px'}
												/>
												<Skeleton
													variant="rectangular"
													width={'100%'}
													height={'88px'}
												/>
												<Skeleton
													variant="rectangular"
													width={'100%'}
													height={'88px'}
												/>
												<Skeleton
													variant="rectangular"
													width={'100%'}
													height={'88px'}
												/>
												<Skeleton
													variant="rectangular"
													width={'100%'}
													height={'88px'}
												/>
												<Skeleton
													variant="rectangular"
													width={'100%'}
													height={'88px'}
												/>
											</>
										)}
									</Stack>
									{matchedCandidates &&
										matchedCandidates.count === 0 &&
										!isLoadingCandidates && (
										<Typography sx={{ textAlign: 'center', m: '12px' }}>
												Наразі кандидати відсутні
										</Typography>
									)}
									{matchedCandidates && matchedCandidates.count > 10 && (
										<Box
											sx={{
												display: 'flex',
												justifyContent: 'center',
												width: '100%',
												marginTop: '22px',
											}}
										>
											<Pagination
												page={page}
												onChange={(_, value) => setPage(value)}
												count={Math.ceil(matchedCandidates.count / 10)}
												color="primary"
											/>
										</Box>
									)}
								</CardContent>
							</Card>
						</Box>
					</CardContent>
				</Card>
			)}
		</Box>
	)
}

export default VacancyPage
